import { Select as AntdSelect, type RefSelectProps } from 'antd'
import { type Ref } from 'react'

import { type SingleOrMultipleSelectProps } from '@/components/types'
import { useSelectExcludeNotExistValues } from '@/libs/hooks'
import { cn } from '@/utils/tailwind.utils'

import { type Entity, type HandleChange } from './select-types'

type Props<T extends Entity> = Omit<
  SingleOrMultipleSelectProps<number, T>,
  'value' | 'onChange'
> & {
  value?: number | number[] | null
  onChange?: HandleChange<T>
}

/**
 * Antd の Select をラップしたコンポーネント
 * - 選択リスト (options) に存在しない値を value に指定した場合は自動的にリセットする処理を追加
 * - 拠点切り替えやデータ無効化により選択リストから指定値が消える可能性がある場合はこのコンポーネントを利用すること
 */
export const Select = <T extends Entity>({
  onChange,
  value,
  options,
  fieldNames,
  optionFilterProp,
  className,
  customRef: ref,
  // see: https://zenn.dev/bicstone/articles/react-forward-ref-generic-type
  ...props
}: Props<T> & { customRef?: Ref<RefSelectProps> }) => {
  const excludeNotExistValues = useSelectExcludeNotExistValues(
    options,
    onChange
  )

  return (
    <AntdSelect
      ref={ref}
      value={excludeNotExistValues(value)}
      options={options}
      fieldNames={fieldNames ?? { label: 'label', value: 'id' }}
      optionFilterProp={optionFilterProp ?? 'label'}
      onChange={onChange}
      className={cn(['min-w-[200px]', className])}
      // 既定は候補幅 = セレクト幅（#963）。呼び出し側の指定を優先するため
      // spread より前に置く。後ろに置くと指定が破棄される（#4805）
      popupMatchSelectWidth={true}
      {...props}
    />
  )
}
